import React from 'react'
import { useTranslation } from 'react-i18next'

import TableCell from '@mui/material/TableCell'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import SortIcon from '../../svgicons/sort'
import AscendIcon from '../../svgicons/ascend'
import DescendIcon from '../../svgicons/descend'
import { SortType } from '../../types/enums/sortType'

interface SortableTableHeadProps {
  children: JSX.Element | Array<JSX.Element> | string
  sortFragment: string
  sortProp: string
  sortType: SortType
  onSort: (sortString: string) => void
}

const SortableTableHead = (props: SortableTableHeadProps): JSX.Element => {
  const { t } = useTranslation()

  const ascFragment = `+${props.sortProp}`
  const descFragment = `-${props.sortProp}`

  const getTooltipText = (): string => {
    if (props.sortType === SortType.String) {
      return props.sortFragment === ascFragment ? 'ordering.stringasc' : 'ordering.stringdesc'
    } else if (props.sortType === SortType.Number) {
      return props.sortFragment === ascFragment ? 'ordering.numberasc' : 'ordering.numberdesc'
    } else if (props.sortType === SortType.Date) {
      return props.sortFragment === ascFragment ? 'ordering.dateasc' : 'ordering.datedesc'
    } else if (props.sortType === SortType.Duration) {
      return props.sortFragment === ascFragment ? 'ordering.durationasc' : 'ordering.durationdesc'
    } else if (props.sortType === SortType.Timestamp) {
      return props.sortFragment === ascFragment ? 'ordering.timestampasc' : 'ordering.timestampdesc'
    }
    return ''
  }

  return (
    <TableCell
      className="sortable-table-header"
      sx={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => {
        if (props.sortFragment === ascFragment) {
          props.onSort(descFragment)
        } else if (props.sortFragment === descFragment) {
          props.onSort('')
        } else {
          props.onSort(ascFragment)
        }
      }}
    >
      <Stack direction="row" alignItems="center">
        {props.children}
        {props.sortFragment !== ascFragment && props.sortFragment !== descFragment && (
          <Tooltip title={t('ordering.sortbycolumn')}>
            <SortIcon />
          </Tooltip>
        )}
        {props.sortFragment === ascFragment && (
          <Tooltip title={t(getTooltipText())}>
            <AscendIcon />
          </Tooltip>
        )}
        {props.sortFragment === descFragment && (
          <Tooltip title={t(getTooltipText())}>
            <DescendIcon />
          </Tooltip>
        )}
      </Stack>
    </TableCell>
  )
}

export default SortableTableHead
